// =====================================================================
// Meal Delivery Bundle Cart Rules
//   - Hide +/- on every meal-delivery line (cart page + mini cart drawer).
//     Rebuy cart hiding lives in custom.css.
//   - Removing ANY meal-delivery line removes ALL meal-delivery lines from
//     the Shopify cart, then reloads the page so every cart UI reflects it.
// =====================================================================
(function () {
  // Rebuy stores the bundle tag as "_rc_bundle_parent". In /cart.js the value
  // is "meal-delivery" (hyphen); in Rebuy's DOM class names it is sanitized to
  // "mealdelivery" (no hyphen). Normalize to letters-only so both forms match,
  // while still excluding any other (non-meal) Rebuy bundle.
  const PARENT_PROP_VALUE = "mealdelivery";
  const COLLECTION_NAME = "Betr Meal Delivery";

  function normalizeParent(value) {
    return String(value == null ? "" : value)
      .toLowerCase()
      .replace(/[^a-z]/g, "");
  }

  let drawerLineIndices = new Set(); // 1-based indices of meal-delivery lines
  let bulkRemoveRunning = false;
  let refreshTimeout = null;

  async function fetchCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return res.json();
  }

  function isMealDeliveryItem(item) {
    return !!(
      item.properties &&
      normalizeParent(item.properties._rc_bundle_parent) === PARENT_PROP_VALUE
    );
  }

  function hideQuantityControls(itemEl) {
    if (itemEl.dataset.mealDeliveryHandled === "1") return;
    itemEl.dataset.mealDeliveryHandled = "1";
    itemEl.querySelectorAll(".quantity__button").forEach((b) => b.remove());
    const input = itemEl.querySelector("input.quantity__input");
    if (input) {
      const p = document.createElement("p");
      p.classList.add("quantity__input");
      p.textContent = input.value;
      input.parentNode.insertBefore(p, input.nextSibling);
      input.style.display = "none";
    }
  }

  function applyCartPageUI() {
    document.querySelectorAll(".cart-items .cart-item").forEach((item) => {
      const cn = item.querySelector(".product-collections .collection-name");
      if (cn && cn.textContent.trim() === COLLECTION_NAME) hideQuantityControls(item);
    });
  }

  function applyMiniCartUI() {
    document
      .querySelectorAll("cart-drawer .cart-item, cart-drawer-items .cart-item")
      .forEach((item) => {
        const indexHolder = item.querySelector("[data-index]");
        const idx = indexHolder ? parseInt(indexHolder.dataset.index, 10) : NaN;
        if (Number.isFinite(idx) && drawerLineIndices.has(idx)) hideQuantityControls(item);
      });
  }

  async function refreshState() {
    try {
      const cart = await fetchCart();
      drawerLineIndices = new Set(
        cart.items
          .map((it, i) => (isMealDeliveryItem(it) ? i + 1 : null))
          .filter((v) => v !== null)
      );
      applyCartPageUI();
      applyMiniCartUI();
    } catch (err) {
      console.error("[meal-delivery] refreshState failed:", err);
    }
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(refreshState, 150);
  }

  function isMealDeliveryLineEl(lineEl) {
    if (lineEl.matches('[class*="property-_rc_bundle_parent:mealdelivery"]')) return true;
    const cn = lineEl.querySelector(".product-collections .collection-name");
    if (cn && cn.textContent.trim() === COLLECTION_NAME) return true;
    const indexHolder = lineEl.querySelector("[data-index]");
    const idx = indexHolder ? parseInt(indexHolder.dataset.index, 10) : NaN;
    return Number.isFinite(idx) && drawerLineIndices.has(idx);
  }

  // Resolves true if the cart was updated (page then reloads); false if there
  // was nothing to remove or the request failed.
  async function removeAllMealDelivery() {
    if (bulkRemoveRunning) return false;
    bulkRemoveRunning = true;
    try {
      const cart = await fetchCart();
      const updates = {};
      cart.items.forEach((it) => {
        if (isMealDeliveryItem(it)) updates[it.key] = 0;
      });
      if (!Object.keys(updates).length) {
        console.warn("[meal-delivery] no meal-delivery lines matched; nothing removed.");
        bulkRemoveRunning = false;
        return false;
      }
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) throw new Error("cart/update.js returned " + res.status);
      window.location.reload();
      return true;
    } catch (err) {
      console.error("[meal-delivery] removeAllMealDelivery failed:", err);
      bulkRemoveRunning = false;
      return false;
    }
  }

  // -------------------------------------------------------------------
  // Confirmation modal: meals are sold as a bundle, so individual lines
  // can't be removed. Clicking remove on ANY meal asks the member to
  // confirm, then clears ALL meal-delivery lines. Non-meal products are
  // never routed here — their native remove is untouched.
  // -------------------------------------------------------------------
  let confirmOpen = false;

  function closeConfirmModal(overlay) {
    overlay.classList.remove("is-open");
    setTimeout(() => overlay.remove(), 200);
    confirmOpen = false;
  }

  function openConfirmModal() {
    if (confirmOpen) return;
    confirmOpen = true;

    const overlay = document.createElement("div");
    overlay.className = "md-confirm-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.innerHTML = [
      '<div class="md-confirm-dialog">',
      '  <div class="md-confirm-icon" aria-hidden="true">&#33;</div>',
      '  <h2 class="md-confirm-title">Remove your meals?</h2>',
      '  <p class="md-confirm-text">Betr meals are sold as a bundle, so individual meals',
      "    can't be removed on their own. Confirming will remove <strong>all</strong>",
      "    meals from your cart. Any non-meal items will stay.</p>",
      '  <div class="md-confirm-actions">',
      '    <button type="button" class="md-confirm-btn md-confirm-btn--danger" data-md-confirm>Remove all meals</button>',
      '    <button type="button" class="md-confirm-btn md-confirm-btn--ghost" data-md-cancel>Keep my meals</button>',
      "  </div>",
      "</div>",
    ].join("");

    document.body.appendChild(overlay);
    // Force reflow so the open transition runs.
    void overlay.offsetWidth;
    overlay.classList.add("is-open");

    const confirmBtn = overlay.querySelector("[data-md-confirm]");
    const cancelBtn = overlay.querySelector("[data-md-cancel]");

    confirmBtn.addEventListener("click", async function () {
      confirmBtn.disabled = true;
      cancelBtn.disabled = true;
      confirmBtn.textContent = "Removing…";
      const ok = await removeAllMealDelivery(); // reloads on success
      if (!ok) {
        // Removal didn't go through — restore the buttons so the member isn't stuck.
        confirmBtn.disabled = false;
        cancelBtn.disabled = false;
        confirmBtn.textContent = "Try again";
      }
    });
    cancelBtn.addEventListener("click", function () {
      closeConfirmModal(overlay);
    });
    overlay.addEventListener("click", function (ev) {
      if (ev.target === overlay) closeConfirmModal(overlay);
    });
    document.addEventListener("keydown", function onKey(ev) {
      if (ev.key === "Escape") {
        closeConfirmModal(overlay);
        document.removeEventListener("keydown", onKey);
      }
    });
    confirmBtn.focus();
  }

  // Capture-phase delegation so we run before native handlers and can preventDefault.
  document.addEventListener(
    "click",
    function (e) {
      const trigger = e.target.closest(
        "cart-remove-button button, cart-remove-button a, .rebuy-cart__flyout-item-remove"
      );
      if (!trigger) return;
      const lineEl = trigger.closest(".cart-item, .rebuy-cart__flyout-item");
      if (!lineEl) return;
      if (isMealDeliveryLineEl(lineEl)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        openConfirmModal();
      }
    },
    true
  );

  function bootstrap() {
    refreshState();
    [document.querySelector("cart-drawer"), document.querySelector("cart-items")]
      .filter(Boolean)
      .forEach((root) => {
        new MutationObserver(scheduleRefresh).observe(root, {
          childList: true,
          subtree: true,
        });
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootstrap);
  } else {
    bootstrap();
  }
})();
$(window).on('load', function(){
  var loggedInFlag = $(".user-logged-in-flag");
    var contentDiv = $("#copilot-widget");

    if (loggedInFlag) {
        contentDiv.fadeIn(1500);
    }

  var pageURL = window.location.href;

  if (pageURL.includes('/blogs/level1') || pageURL.includes('/blogs/level2')) {
    console.log('Found matching blog page');

    // Select the main container and its article items
    var $container = $('ul.blog-articles.blog-articles__grid');
    if ($container.length === 0) {
      console.log('Main container not found');
      return;
    }

    var $items = $container.find('li.blog-articles__article');
    console.log('Found ' + $items.length + ' articles to sort');

    if ($items.length === 0) {
      console.log('No blog articles found');
      return;
    }

    // Sort the items based on the numeric value after "Day" in the title
    var sortedItems = $items.sort(function (a, b) {
      var aTitle = $(a).find('h2.article-card__title').text().trim();
      var bTitle = $(b).find('h2.article-card__title').text().trim();

      console.log('Sorting:', aTitle, 'and', bTitle);

      // Extract "Day" number using regex
      var aMatch = aTitle.match(/Day\s*(\d+)/i);
      var bMatch = bTitle.match(/Day\s*(\d+)/i);

      var aNum = aMatch ? parseInt(aMatch[1], 10) : 0;
      var bNum = bMatch ? parseInt(bMatch[1], 10) : 0;

      return aNum - bNum;
    });

    // Replace the current items with the sorted ones
    $container.empty().append(sortedItems);
    console.log('Articles sorted and reappended');
  }
  $('a[href^="#"]').on('click', function (e) {
      e.preventDefault();
      var target = $(this.getAttribute('href'));

      if (target.length) {
          $('html, body').animate({
              scrollTop: target.offset().top
          }, 600); // 600ms duration for the scroll
      }
  });
});


if(refTkUsr){
function sendEvent(eventType, description = '', url = '') {
  var fetchUrl = 'https://jxwbkazom9.execute-api.us-east-2.amazonaws.com/prod/api/users/' + refTkUsr + '/events';
  var token = '08da4cfd4c4941df8d6adc7f75a505c2';

  // Extract browser and device information
  var userAgent = navigator.userAgent;
  var browser = getBrowserInfo(userAgent);
  var device = getDetailedDeviceInfo(userAgent);

  var data = {
      event: eventType,
      description: description,
      url: url || window.location.href,
      browser: browser,
      device: device
  };

  $.ajax({
      url: fetchUrl,
      type: 'POST',
      headers: {
          'Content-Type': 'application/json',
          'Authorization': token
      },
      data: JSON.stringify(data),
      success: function(response) {
          console.log('event:', eventType);
      },
      error: function(xhr, status, error) {
          console.error('Status:', status);
          console.error('Error Message:', error);
          console.error('XHR:', xhr);
          console.error('Response Headers:', xhr.getAllResponseHeaders());
      }
  });
}

// Helper function to determine browser info
function getBrowserInfo(userAgent) {
  if (userAgent.indexOf("Chrome") > -1) return "Chrome";
  if (userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") === -1) return "Safari";
  if (userAgent.indexOf("Firefox") > -1) return "Firefox";
  if (userAgent.indexOf("MSIE") > -1 || userAgent.indexOf("Trident/") > -1) return "Internet Explorer";
  if (userAgent.indexOf("Edge") > -1) return "Edge";
  return "Unknown";
}

// Helper function to get detailed device info
function getDetailedDeviceInfo(userAgent) {
  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (/iPad/i.test(userAgent)) return 'iPad';
  if (/Macintosh/i.test(userAgent)) return 'Mac';
  if (/Windows NT/i.test(userAgent)) return 'Windows PC';
  if (/Android/i.test(userAgent)) {
      const match = userAgent.match(/Android\s([0-9\.]+);?\s?([^\);]+)/);
      if (match && match[2]) return 'Android - ' + match[2].trim();
      return 'Android Device';
  }
  if (/Linux/i.test(userAgent)) return 'Linux';
  return 'Unknown Device';
}
}
